import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ShieldCheck, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import BrandMark from "@/components/BrandMark";

/**
 * Retailer-facing claim page. Reached via /claim/<token>. Backend returns
 * 404 for invalid/expired/reused tokens so we never disclose the retailer
 * catalogue through token guessing.
 */
export default function ClaimAccount() {
  const { token } = useParams();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [contactName, setContactName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api
      .get(`/claim/${token}`)
      .then((r) => {
        setPreview(r.data);
        setEmail(r.data.email || "");
        setContactName(r.data.contact_name || "");
      })
      .catch(() => setError("This invitation is invalid or has expired. Ask the FloorMatch team for a fresh link."))
      .finally(() => setLoading(false));
  }, [token]);

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const r = await api.post(`/claim/${token}`, {
        email: email.trim().toLowerCase(),
        password,
        contact_name: contactName.trim(),
      });
      // Backend has set the httpOnly session cookie. Full reload so
      // AuthContext re-runs /auth/me and lands the retailer on their
      // dashboard.
      void r;
      toast.success("Welcome to FloorMatch");
      window.location.assign("/retailer");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="min-h-screen fm-grain grid place-items-center p-4" data-testid="claim-page">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
        <Link to="/" className="inline-flex items-center gap-2 mb-4">
          <BrandMark className="w-14 h-14" />
        </Link>
        {loading ? (
          <div className="text-slate-500">Checking your invitation…</div>
        ) : error && !preview ? (
          <div className="space-y-4" data-testid="claim-error">
            <div className="grid place-items-center w-12 h-12 rounded-full bg-red-100 text-red-700">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <h1 className="font-display font-bold text-2xl text-slate-900">Invitation not valid</h1>
            <p className="text-sm text-slate-600">{error}</p>
            <Link to="/" className="inline-block fm-btn-primary">Back to FloorMatch</Link>
          </div>
        ) : (
          <>
            <div className="mb-4">
              <div className="inline-flex items-center gap-1 text-xs font-semibold text-[#2A5A3B] bg-[#2A5A3B]/10 rounded-full px-2 py-1">
                <ShieldCheck className="w-3.5 h-3.5" /> Verified invitation
              </div>
              <h1 className="font-display font-bold text-2xl text-slate-900 mt-2">
                Claim <span className="text-[#E05915]">{preview.business_name}</span>
              </h1>
              <p className="text-sm text-slate-600 mt-1">
                Set your login and you'll take control of the {preview.business_name} listings
                already on FloorMatch — no duplicates.
              </p>
            </div>

            <form onSubmit={submit} className="space-y-3" data-testid="claim-form">
              <label className="block">
                <span className="fm-label">Your name</span>
                <input
                  required
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  className="fm-input"
                  data-testid="claim-name"
                />
              </label>
              <label className="block">
                <span className="fm-label">Email you'll sign in with</span>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="fm-input"
                  data-testid="claim-email"
                />
              </label>
              <label className="block">
                <span className="fm-label">Choose a password (min 8 chars)</span>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="fm-input"
                  data-testid="claim-password"
                />
              </label>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm" data-testid="claim-form-error">
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="fm-btn-primary w-full disabled:opacity-70 inline-flex items-center justify-center gap-2"
                data-testid="claim-submit"
              >
                <CheckCircle2 className="w-4 h-4" />
                {submitting ? "Claiming…" : "Claim my account"}
              </button>
              <p className="text-xs text-slate-500 text-center">
                By claiming you agree that all listings under {preview.business_name} on
                FloorMatch are yours to manage.
              </p>
            </form>
          </>
        )}
      </div>
    </main>
  );
}
